import { createDebug, FRAME_ID } from '@/utils/debug'
import { queryActiveView, readSelectedSlideId } from '@/services/office/helpers'
import {
  resolveActiveSlideOwnership,
  SLIDE_OWNERSHIP,
} from '@/services/office/current-slide'

// "Is the slide the user has SELECTED IN THE EDITOR this frame's?" — the edit-view counterpart to
// current-slide.js, which answers the same question for the Slide Show.
//
// Why a separate module rather than another handler set on startTagSlideFocusPolling: that poll's
// three callbacks are show semantics, not identity. onMine auto-starts the slide, onOther stands the
// frame down and resets its quiz, onNoAhaSlideOwner ENDS a running quiz. None of those are things a
// click in the editor may do, and letting an editor frame reach the stop path is a live-audience
// hazard rather than merely wasted work. The machinery around them — the MINE -> not-MINE edge latch,
// NO_OWNER_STOP_DELAY_MS, the show-owner claim veto — exists for the same reason and has no meaning
// here. What IS shared is the part worth sharing: resolveActiveSlideOwnership, which is already
// view-agnostic and already platform-forked.
//
// WHAT IT COSTS, and why it is an event rather than a poll. current-slide.js runs a 250ms timer for
// the length of a show. An editing session is hours, not minutes, so the same timer is a different
// proposition. DocumentSelectionChanged was probed on desktop first (see the spike this replaces):
// it fires in edit view, and reports both halves this needs — the Common-API id AND the 1-based
// index. So the timer is replaced by a signal that fires when a human clicks.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:edit-slide-owner'
const log = createDebug('ppt:edit-slide-owner')

// Kept a LEAF, for the reason current-slide.js documents on startTagSlideFocusPolling: @/vuex/store's
// module graph reaches back into services/office, so importing it here would close a cycle. The store
// write is injected by the wiring in services/office/index.js instead — which is also what keeps the
// verdict's SHAPE (three-valued) separate from where it happens to be stored (pptAddin module state).
let onVerdict = () => {}

// The last selection this frame ACTED on. Two jobs, and the second is the one that matters.
//
// De-dup. DocumentSelectionChanged fires on selection changes WITHIN a slide — clicking a shape or a
// text box — not just on slide changes. Measured directly on desktop: two events four seconds apart,
// both reporting the same slide (id 257, index 2). Without this, every textbox click would cost a
// PowerPoint.run tag batch plus a document.settings refresh.
//
// It is also why this module does NOT pass the id down as `knownCommonSlideId`. That opens
// resolveActiveSlideOwnership's cached fast path, whose safety argument is that it is "strictly
// one-way, can only short-circuit TO MINE" — sound while a show holds the deck FROZEN, where a wrong
// cache can only cancel a stop. The editor is the session where the deck is mutable by definition:
// rebind this frame to a different slide and a cached MINE would go on claiming a slide that is no
// longer ours, with nothing running to correct it. De-duplicating here gets the same saving without
// borrowing that assumption.
let lastResolvedSelectionId = null
let selectionHandler = null
let inFlight = false

export const EDIT_SLIDE_OWNERSHIP_UNKNOWN = null

const reportVerdict = (isMine, commonSlideId) => {
  onVerdict(isMine, commonSlideId)
}

const resolveForSelection = async (selectedId) => {
  // No knownCommonSlideId — see lastResolvedSelectionId. Desktop re-reads the selection inside the
  // confirm regardless (its tag batch needs the INDEX, which only that read provides), so passing it
  // would buy nothing there but the cache this deliberately declines.
  const { ownership, commonSlideId } = await resolveActiveSlideOwnership()

  // The verdict must be about the slide we asked about. Because the desktop confirm issues its own
  // selection read, a second click landing in between returns an answer about a DIFFERENT slide —
  // and resolveActiveSlideOwnership is single-flighted, so a caller can be handed a promise it did
  // not start. commonSlideId is what makes that detectable (current-slide.js:430); only the caller
  // knows which slide it asked about. A mismatch is discarded rather than applied: the newer click
  // has its own event already on the way, and applying a stale verdict is exactly the
  // wrong-frame-claims-the-slide failure this whole area exists to remove.
  if (commonSlideId !== null && String(commonSlideId) !== String(selectedId)) {
    log('verdict is about a different slide -> discarded | asked =', selectedId,
      '| answered =', commonSlideId)
    return
  }

  // UNKNOWN stays UNKNOWN rather than collapsing to false. The two are different facts — "the
  // selected slide is provably not mine" versus "nobody could tell" — and current-slide.js keeps
  // them apart for a reason its SLIDE_OWNERSHIP comment spells out: the callers want opposite things
  // from an unanswerable question. A consumer that treats null as false is choosing that, visibly.
  let isMine = EDIT_SLIDE_OWNERSHIP_UNKNOWN
  if (ownership === SLIDE_OWNERSHIP.MINE) isMine = true
  if (ownership === SLIDE_OWNERSHIP.OTHER) isMine = false

  // Recorded only once a verdict has actually been applied, so a discarded or thrown attempt leaves
  // the next event free to re-ask about the same slide.
  lastResolvedSelectionId = selectedId
  log('selection =', selectedId, '| ownership =', ownership, '| isMine =', isMine,
    '| frame =', FRAME_ID)
  reportVerdict(isMine, selectedId)
}

const handleSelectionChanged = async () => {
  // The event is a PROMPT, never a verdict — the rule this module family already follows for
  // ActiveViewChanged (services/office/index.js: "The event is a prompt to ASK the host, never the
  // answer"). It carries no slide information of its own, so everything below is a fresh ask.

  // Observed firing occasionally in read view, so the view is confirmed per event rather than
  // assumed. Asked of the HOST rather than read from store.state.addinPPTActiveView, which is a
  // lagging async mirror — the same reasoning as isHostInReadView in services/office/index.js.
  // A read-view event belongs to the Slide Show flow, which owns its own poll; answering it here
  // would put two independent writers on the same question.
  const view = await queryActiveView()
  if (view !== 'edit') {
    log('ignored: not edit view | view =', view)
    return
  }

  // One confirm at a time. resolveActiveSlideOwnership single-flights its own desktop batch, but
  // that guard would hand this module a verdict about whichever slide the IN-FLIGHT call started
  // on — which the commonSlideId check above would then discard, having paid for it. Dropping the
  // event outright is cheaper and loses nothing: the selection is a level, not an edge, so the next
  // click re-asks with the current value.
  if (inFlight) {
    log('ignored: confirm already in flight')
    return
  }

  readSelectedSlideId(async (selectedId, error) => {
    if (error || !selectedId) {
      log('ignored: selection unreadable |', error)
      return
    }
    if (String(selectedId) === String(lastResolvedSelectionId)) {
      log('ignored: same slide as last verdict | selection =', selectedId)
      return
    }
    inFlight = true
    try {
      await resolveForSelection(selectedId)
    } catch (err) {
      // Never throws out of a host event handler. A failed confirm is "could not tell", which is
      // already representable, and lastResolvedSelectionId is untouched so the next click re-asks.
      log('confirm threw', err)
    } finally {
      inFlight = false
    }
  })
}

// How long to wait before re-asking a baseline that came back UNKNOWN. Covers the one window where
// this module's desktop batch can contend with another: resizeAddinToFillSlide opens a PowerPoint.run
// batch of its own, and it is chained off the cold-load promise rather than sequenced with the
// registration below, so the two can overlap. Two concurrent batches in a frame deadlock each other's
// context.sync() (AHA-46375) — but readActiveSlideBinding bounds its own read
// (ACTIVE_SLIDE_BINDING_TIMEOUT_MS), so the failure surfaces as a bounded UNKNOWN rather than a hang,
// and re-asking once after the resize has finished is enough to recover it.
const BASELINE_RETRY_DELAY_MS = 2000

// DocumentSelectionChanged fires on CHANGE, so a frame that cold-loads on the slide the user already
// has selected receives nothing at all — the commonest case there is, since inserting the add-in
// selects its slide. Without a baseline that frame would sit at the initial "nobody could tell"
// until the user clicked away and back, which any consumer reading the verdict would see as a
// settled fact rather than the absence of one.
//
// Deliberately unawaited by the caller: this is one host round trip on a cold-load path where the
// frame has a presentation to render, and no consumer of the verdict is blocked on it.
const resolveBaseline = (attempt = 1) => {
  readSelectedSlideId(async (selectedId, error) => {
    if (error || !selectedId) {
      log('baseline: selection unreadable |', error)
      return
    }
    inFlight = true
    try {
      await resolveForSelection(selectedId)
    } catch (err) {
      log('baseline threw', err)
    } finally {
      inFlight = false
    }
    // resolveForSelection only records lastResolvedSelectionId once a verdict was APPLIED, so this
    // reads "did the baseline settle on anything" without needing a second return channel.
    if (lastResolvedSelectionId === null && attempt === 1) {
      log('baseline unresolved -> re-asking in', BASELINE_RETRY_DELAY_MS, 'ms')
      setTimeout(() => resolveBaseline(2), BASELINE_RETRY_DELAY_MS)
    }
  })
}

// Idempotent, matching setupAutoStartViewMachine's own teardown: detectActiveViewChange() can run
// more than once in a frame, and a duplicate handler would double every confirm.
export const stopEditModeSlideOwnership = () => {
  const doc = window.Office?.context?.document
  const eventType = window.Office?.EventType?.DocumentSelectionChanged
  if (doc && eventType && selectionHandler) {
    doc.removeHandlerAsync(eventType, { handler: selectionHandler })
  }
  selectionHandler = null
  lastResolvedSelectionId = null
  inFlight = false
  onVerdict = () => {}
}

// `handlers.onVerdict(isMine, commonSlideId)` — isMine is true / false / null (unknown).
export const startEditModeSlideOwnership = (handlers = {}) => {
  stopEditModeSlideOwnership()

  const doc = window.Office?.context?.document
  const eventType = window.Office?.EventType?.DocumentSelectionChanged
  if (!doc || !eventType || typeof doc.addHandlerAsync !== 'function') {
    log('DocumentSelectionChanged unavailable on this host -> edit-view ownership disabled')
    return
  }

  onVerdict = typeof handlers.onVerdict === 'function' ? handlers.onVerdict : () => {}
  selectionHandler = () => { handleSelectionChanged() }
  doc.addHandlerAsync(eventType, selectionHandler, (result) => {
    const failed = window.Office?.AsyncResultStatus
      && result?.status === window.Office.AsyncResultStatus.Failed
    log('DocumentSelectionChanged registered =', !failed, '| frame =', FRAME_ID,
      failed ? `| error = ${result?.error?.message}` : '')
    if (failed) {
      selectionHandler = null
      return
    }
    // Only once the handler is live, so a baseline and the first real event cannot both be in
    // flight against the same still-unregistered frame.
    resolveBaseline()
  })
}
